import { useApi } from '@hero/context';
import type {
  UseProfileBulkImportApi,
  UseProfileCreateApi,
  UseProfileDeleteApi,
  UseProfileGetOneApi,
  UseProfileListApi,
  UseProfileSuggestApi,
  UseProfileUpdateApi,
} from './types';
import { useInfiniteQueryAPI, useMutationAPI, useQueryAPI } from '../../utils';

/**
 * Brand directory list. Read-only for any signed-in user; admin writes
 * live in the admin SDK and surface via the create / update / delete /
 * bulk-import hooks below.
 *
 * Hook API: {@link UseProfileListApi}
 */
export const useProfileListApi: UseProfileListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'profile',
    apiFn: api.profiles.getAll,
    query,
    apiConfig,
  });
};

/** Get one Profile by `_id`. Disabled while `id` is undefined. */
export const useProfileApi: UseProfileGetOneApi = (id, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'profile',
    apiFn: api.profiles.getById,
    singleIdQuery: id,
    query: undefined,
    apiConfig,
    isEnabled: () => !!id,
  });
};

/** Admin: create a Profile. Hook API: {@link UseProfileCreateApi} */
export const useProfileCreateApi: UseProfileCreateApi = apiConfig => {
  const api = useApi();
  // `create` only exists on the admin SDK; cast through unknown for the
  // shared hook so customer/carrier SDKs that don't expose admin writes
  // still type-check. Calls outside admin will fail at runtime with a
  // clear "method not found" error.
  return useMutationAPI({
    entity: 'profile',
    apiFn: (api.profiles as unknown as { create: ProfileCreateLike }).create,
    apiConfig,
  });
};

/** Admin: update a Profile. */
export const useProfileUpdateApi: UseProfileUpdateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'profile',
    apiFn: (api.profiles as unknown as { update: ProfileUpdateLike }).update,
    apiConfig,
  });
};

/** Admin: delete a Profile. */
export const useProfileDeleteApi: UseProfileDeleteApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'profile',
    apiFn: (api.profiles as unknown as { delete: ProfileDeleteLike }).delete,
    apiConfig,
  });
};

/** Admin: bulk-import curated Profile rows. */
export const useProfileBulkImportApi: UseProfileBulkImportApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'profile',
    apiFn: (api.profiles as unknown as { bulkImport: ProfileBulkImportLike }).bulkImport,
    apiConfig,
  });
};

/**
 * Tenant "suggest a new brand" — pins `source: 'suggested'`
 * server-side and lives on every SDK (the underlying endpoint is
 * open to any signed-in caller). Used by the
 * {@link EntityDirectoryPicker} bottom-row affordance.
 */
export const useProfileSuggestApi: UseProfileSuggestApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'profile',
    apiFn: api.profiles.suggest,
    apiConfig,
  });
};

// Loose function types used for the admin-only cast above.
type ProfileCreateLike = (args: unknown) => Promise<unknown>;
type ProfileUpdateLike = (args: unknown) => Promise<unknown>;
type ProfileDeleteLike = (args: unknown) => Promise<unknown>;
type ProfileBulkImportLike = (args: unknown) => Promise<unknown>;
